import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Calendar,
  BookOpen,
  QrCode,
} from "lucide-react";
import { getMember } from "@libraryadmin/services/api";
import { useState } from "react";
import { MemberIDCardModal } from "@libraryadmin/components/members/MemberIDCardModal";

export default function MemberDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showQRModal, setShowQRModal] = useState(false);

  const {
    data: member,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["member", id],
    queryFn: () => getMember(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <DashboardLayout title="Member Details">
        <div className="space-y-6 p-6">
          <Skeleton className="h-40 w-full rounded-xl" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-64 rounded-xl" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError || !member) {
    return (
      <DashboardLayout title="Member Details">
        <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-4">
          <h2 className="text-xl font-bold text-destructive">
            Member Not Found
          </h2>
          <p className="text-muted-foreground">
            The requested member could not be found or does not exist.
          </p>
          <Button onClick={() => navigate("/library-admin/members")}>
            Back to Members
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Member Details"
      subtitle={`Viewing details for ${member.name}`}
    >
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/library-admin/members")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Members
        </Button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Profile Card */}
          <div className="col-span-1 space-y-6 lg:col-span-1">
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="bg-primary/5 p-6 text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-3xl font-bold text-primary ring-4 ring-primary/20">
                  {member.name.charAt(0)}
                </div>
                <h3 className="text-xl font-bold">{member.name}</h3>
                <span className="font-mono text-sm text-muted-foreground">
                  {member.memberId}
                </span>
                <div className="mt-3">
                  <Badge
                    variant={
                      member.status === "active" ? "default" : "destructive"
                    }
                  >
                    {member.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div className="p-6">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => setShowQRModal(true)}
                >
                  <QrCode className="mr-2 h-4 w-4" /> View ID Card
                </Button>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="col-span-1 space-y-6 lg:col-span-2">
            <div className="rounded-xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-4">
                <h3 className="font-semibold">Contact Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-primary" />
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Phone
                    </span>
                    <span className="text-sm font-medium">{member.phone}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary" />
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </span>
                    <span className="text-sm font-medium">{member.email}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:col-span-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Address
                    </span>
                    <span className="text-sm font-medium">
                      {member.address}, {member.city}, {member.state} -{" "}
                      {member.pincode}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-4">
                <h3 className="font-semibold">Library Activity</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Books Issued
                    </span>
                    <span className="text-sm font-medium">
                      {member.booksIssued} books currently
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-primary" />
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Joined Date
                    </span>
                    <span className="text-sm font-medium">
                      {member.joinedDate}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MemberIDCardModal
        member={member}
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        isAutoGenerated={false}
      />
    </DashboardLayout>
  );
}
